import { observer } from 'mobx-react-lite';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRoute } from '@react-navigation/native';
import { styled, SxProp } from 'dripsy';
import { View } from 'react-native';
import type { StripeElementsOptions } from '@stripe/stripe-js';
import {
  EdgeInsets,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';

import {
  BaseText,
  ButtonStyleEnum,
  ScreenTopBar,
  Separator,
  BreadcrumbBar as BaseBreadcrumbBar,
  ScrollView,
} from '@components';
import {
  FeedbackNavigatorRoutePropType,
  screens,
  ScreensNameType,
} from '@navigation';
import { useTracksModel } from '@models/Tracks/TracksModel';
import { useNavOptionsSet } from '@hooks';
import { CreditsPaymentForm } from './components/CreditsPaymentForm';
import { StripeElementsProvider } from '@context';
import { PAYMENT_METHOD_TYPES } from '@constants';

const ScrollViewContainer = styled(ScrollView)({
  flex: 1,
  bg: '$background0',
});

function contentContainerStyle(props: EdgeInsets): SxProp {
  return (theme) => ({
    flexGrow: 1,
    pb: theme.space.$6 + props.bottom,
  });
}

const ContentContainer = styled(View)({
  width: 415,
  mx: [null, '5%'],
});

const BreadcrumbBar = styled(BaseBreadcrumbBar)({
  mx: [null, '5%'],
});

const TopContainer = styled(View)({
  borderWidth: 1,
  borderColor: '$primary300',
  borderRadius: 12,
  alignItems: 'center',
  justifyContent: 'center',
  py: '$5',
});

const Title = styled(BaseText)({
  variant: 'text.large',
  color: '$text400',
});

const Price = styled(BaseText)({
  variant: 'text.large',
  color: '$heading800',
});

const Subtitle = styled(BaseText)({
  variant: 'text.xsmall',
  color: '$text400',
});

export type ConfirmationModalButtonType = {
  text: string;
  style?: ButtonStyleEnum;
};

type RoutePropType = FeedbackNavigatorRoutePropType<'CreditsPayment'>;

const BREADCRUMB_ROUTES: ScreensNameType[] = [
  screens.TrackFeedback,
  screens.CreditsPayment,
];

export const CreditsPaymentScreen = observer(() => {
  const { t } = useTranslation();
  const tracks = useTracksModel();
  const route = useRoute<RoutePropType>();
  const insets = useSafeAreaInsets();

  useNavOptionsSet({ title: t('creditsPayment.title') });

  const elementOptions = useMemo<StripeElementsOptions>(() => {
    return {
      mode: 'payment',
      amount: Math.round(Number(route.params.price ?? 0) * 100),
      currency: 'gbp',
      paymentMethodTypes: PAYMENT_METHOD_TYPES,
    };
  }, [route.params.price]);

  return (
    <ScrollViewContainer
      contentContainerSx={contentContainerStyle(insets)}
    >
      <BreadcrumbBar routes={BREADCRUMB_ROUTES} />
      <ScreenTopBar
        title={t('creditsPayment.title')}
        isSearchVisible={false}
      />
      <Separator size="$8" />

      <ContentContainer>
        <TopContainer>
          <Title>
            {t('creditsPayment.infoTitle', {
              count: route.params.count,
            })}{' '}
            <Price>
              {tracks.feedback.getFormattedPrice(route.params.price)}
            </Price>
          </Title>
          <Subtitle>
            {t('creditsPayment.subtitle', {
              date: tracks.feedback.getFormattedDate(
                route.params.expirationDate,
              ),
            })}
          </Subtitle>
        </TopContainer>
        <Separator size="$5" />
        <StripeElementsProvider options={elementOptions}>
          <CreditsPaymentForm />
        </StripeElementsProvider>
      </ContentContainer>
    </ScrollViewContainer>
  );
});
